import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Form, Card, Badge, Alert, Modal as RBModal, Toast, ToastContainer } from 'react-bootstrap';
import Overlay from 'react-bootstrap/Overlay';
import { useQueryClient } from '@tanstack/react-query';
import DataTable, { TableColumn } from 'react-data-table-component';
import type { Customer, CustomerStatus, CustomerType, Corporate, ContactDetail, Director } from '../../../services/schemas/customerServiceSchemas';
import { formatCurrency, formatDate } from '../../../utils/formatters';
import { useGetCustomers, useSearchCustomers, useActivateCustomer, useDeactivateCustomer, useBlacklistCustomer, useUnblacklistCustomer } from '../../../services/customerService';
import { LeadLoanType, LeadContactDetailsPreferredChannel } from '../../../services/schemas/loanServiceSchemas';
import { useGetLedgerAccountsByCustomer } from '../../../services/loanService';
import type { SearchCustomersParams, SearchCustomersStatus } from '../../../services/schemas/customerServiceSchemas';
import './LoanCustomers.css';
import Modal from 'bootstrap/js/dist/modal';

// Add custom styles
const customStyles = {
  // Remove the verticalText style since we won't need it anymore

  // DataTable custom styles
  dataTable: {
    rows: {
      style: {
        minHeight: '45px',
      }
    },
    headCells: {
      style: {
        fontWeight: 600,
        paddingLeft: '8px',
        paddingRight: '8px',
        fontSize: '14px',
      }
    },
    cells: {
      style: {
        paddingLeft: '8px',
        paddingRight: '8px',
      }
    }
  }
};

interface BusinessCustomer extends Omit<Customer, 'info'> {
    type: 'CORPORATE';
    info: Corporate & {
        directors?: Director[];
    };
    contacts?: ContactDetail[];
}

interface ApiError {
    message: string;
}

type LoanCustomerSearchParams = SearchCustomersParams & {
    customerNumber?: string;
};

type CustomerActionType = 'activate' | 'deactivate' | 'blacklist' | 'unblacklist';

const BLACKLISTED_STATUSES = new Set(['SUSPENDED', 'BLACKLISTED']);
const LOAN_APPLICATION_RESTRICTED_STATUSES = new Set(['INACTIVE', ...BLACKLISTED_STATUSES]);

/**
 * Outstanding balance for one customer, fetched when the row renders.
 *
 * Declared at module scope and memoised on purpose. Defined inside the page component it was a new
 * component type on every render, so React unmounted and remounted every cell and refetched the
 * ledger each time - a page of ten customers cost well over a hundred requests.
 */
export const CustomerBalanceCell: React.FC<{
    customerNumber?: string;
    onBalance?: (customerNumber: string, amount: number) => void;
}> = React.memo(({ customerNumber, onBalance }) => {
    const { data, isLoading, error } = useGetLedgerAccountsByCustomer(
        { customerNumber: customerNumber || '' },
        { query: { enabled: !!customerNumber } }
    );
    const accounts = Array.isArray(data) ? data : [];
    const totalOutstanding = accounts.reduce((sum: number, acc: any) => sum + (acc.totalOutstanding || acc.outstandingTotal || 0), 0);

    useEffect(() => {
        if (customerNumber && !isLoading && !error) {
            onBalance?.(customerNumber, totalOutstanding);
        }
    }, [customerNumber, isLoading, error, totalOutstanding, onBalance]);

    if (!customerNumber) return <span className="text-muted">—</span>;
    if (isLoading) return <span className="spinner-border spinner-border-sm text-primary" role="status" aria-hidden="true"></span>;
    if (error) return <span className="text-muted">—</span>;

    return (
        <span className={totalOutstanding > 0 ? 'fw-bold text-danger' : 'fw-semibold text-success'}>
            {formatCurrency(totalOutstanding)}
        </span>
    );
});

export const customerActionsPopperConfig = {
    strategy: 'fixed' as const,
    modifiers: [
        { name: 'offset', options: { offset: [0, 8] as [number, number] } },
        {
            name: 'flip',
            options: {
                boundary: 'viewport',
                rootBoundary: 'viewport',
                padding: 8,
                fallbackPlacements: ['top-end', 'bottom-end'],
            },
        },
        {
            name: 'preventOverflow',
            options: {
                boundary: 'viewport',
                rootBoundary: 'viewport',
                padding: 8,
                altAxis: true,
            },
        },
    ],
};

const LoanCustomers: React.FC = () => {
    const [page, setPage] = useState(1);
    const [size, setSize] = useState(10);
    const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
    const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
    const [formValues, setFormValues] = useState<LoanCustomerSearchParams>({
        customerNumber: '',
        registeredName: '',
        registrationNumber: '',
        status: undefined,
        createdDateFrom: '',
        createdDateTo: '',
        page: 0,
        size: 10
    });
    const [searchParams, setSearchParams] = useState<LoanCustomerSearchParams | null>(null);
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const activateCustomerMutation = useActivateCustomer();
    const deactivateCustomerMutation = useDeactivateCustomer();
    const blacklistCustomerMutation = useBlacklistCustomer();
    const unblacklistCustomerMutation = useUnblacklistCustomer();

    const [toastState, setToastState] = useState<{ show: boolean; bg: 'success' | 'danger'; message: string } | null>(null);

    const [confirmState, setConfirmState] = useState<{
        show: boolean;
        type: CustomerActionType | null;
        id?: string;
        name?: string;
        loading?: boolean;
    }>({ show: false, type: null, loading: false });

    const openConfirm = (type: CustomerActionType, id?: string, name?: string) => {
        setConfirmState({ show: true, type, id, name, loading: false });
    };
    const closeConfirm = (force = false) => {
        setConfirmState(prev => (!force && prev.loading) ? prev : { ...prev, show: false, loading: false });
    };

    const ALL_CUSTOMERS_PAGE_SIZE = 10000;


    

    // Initial load using getCustomers (fetch all for client-side pagination)
    const { data: initialData, isLoading: isInitialLoading, error: initialError } = useGetCustomers({
        page: 0,
        size: ALL_CUSTOMERS_PAGE_SIZE,
    }) as { data: any; isLoading: boolean; error: ApiError | null };

        // Search using searchCustomers - only called when searchParams is set
    const { data: searchData, isLoading: isSearchLoading, error: searchError } = useSearchCustomers(
        searchParams || { page: 0, size: ALL_CUSTOMERS_PAGE_SIZE }
    ) as { data: any; isLoading: boolean; error: ApiError | null };

    // Debug: log the search data
    useEffect(() => {
        if (searchData?.content) {
            const statuses = searchData.content.map((c: Customer) => c.status);
            console.log('[LoanCustomers] Search data statuses:', statuses);
        }
    }, [searchData]);

    // Use either search results or initial data
    const customersData = searchParams ? searchData : initialData;
    const isLoading = searchParams ? isSearchLoading : isInitialLoading;
    const error = searchParams ? searchError : initialError;

    // Debug: log the statuses coming back from the API
    useEffect(() => {
        if (customersData?.content) {
            const statuses = customersData.content.map((c: Customer) => c.status);
            console.log('[LoanCustomers] Backend statuses sample:', statuses.slice(0, 10), '... total:', statuses.length);
        }
    }, [customersData]);

    // Filter to only show corporate customers
    const corporateCustomers = (customersData?.content?.filter(
        (customer: Customer) => customer.type === 'CORPORATE' && customer.info?.type === 'CORPORATE'
    ) || []) as BusinessCustomer[];

    const allCorporateCustomers = (initialData?.content?.filter(
        (customer: Customer) => customer.type === 'CORPORATE' && customer.info?.type === 'CORPORATE'
    ) || []) as BusinessCustomer[];

        const customerNumberQuery = searchParams?.customerNumber?.trim().toLowerCase();
    const statusQuery = searchParams?.status;
    
    const filteredCorporateCustomers = corporateCustomers.filter((customer) => {
        const matchesCustomerNumber = customerNumberQuery
            ? (customer.customerNumber || '').toLowerCase().includes(customerNumberQuery)
            : true;
        const matchesStatus = statusQuery
            ? normalizedStatus(customer.status) === statusQuery
            : true;
        return matchesCustomerNumber && matchesStatus;
    });

    // Total corporate customers available (client-side pagination).
    const totalCorporateCustomers = filteredCorporateCustomers.length;

    // Get error message
    const errorMessage = error?.message || 'An error occurred while fetching customers. Please try again later.';

    const handleViewAccounts = (customerNumber: string) => {
        navigate(`/customers/${customerNumber}/accounts`);
    };

    const handleEditCustomer = (id: string) => {
        navigate(`/loan-customers/${id}/edit`);
    };

    const handleViewCustomer = (id: string) => {
        navigate(`/loan-customers/${id}/view`);
    };

    const normalizedStatus = (status?: CustomerStatus | string) => (status || '').toString().toUpperCase();
    const displayStatus = (status?: CustomerStatus | string) => {
        const current = normalizedStatus(status);
        if (BLACKLISTED_STATUSES.has(current)) return 'BLACKLISTED';
        if (!current) return 'UNKNOWN';
        return current;
    };
    const isRestrictedStatus = (status?: CustomerStatus | string) => {
        const current = normalizedStatus(status);
        return LOAN_APPLICATION_RESTRICTED_STATUSES.has(current);
    };

    const showRestrictionToast = (status?: CustomerStatus | string) => {
        const current = normalizedStatus(status);
        const message = current === 'INACTIVE'
            ? 'Customer is inactive'
            : 'Customer is blacklisted';

        setToastState({ show: true, bg: 'danger', message });
    };

    const handleApplyLoan = (row: BusinessCustomer) => {
        if (isRestrictedStatus(row.status)) {
            showRestrictionToast(row.status);
            return;
        }

        navigate('/loan-application/new', {
            state: {
                registrationNumber: row.info?.registrationNumber || '',
                customerNumber: row.customerNumber || '',
                customerId: row.id || '',
                customerName: row.info?.registeredName || ''
            }
        });
    };

    const refreshCustomerQueries = async (id: string) => {
        await Promise.all([
            queryClient.invalidateQueries({ queryKey: ['/customer/api/v1/all'] }),
            queryClient.invalidateQueries({ queryKey: ['/customer/api/v1/search'] }),
            queryClient.invalidateQueries({ queryKey: [`/customer/api/v1/customers/${id}`] }),
        ]);
    };

        const handleActivateCustomer = async (id: string) => {
        try {
            await activateCustomerMutation.mutateAsync({ customerId: id });
            await refreshCustomerQueries(id);
            setToastState({ show: true, bg: 'success', message: 'Customer activated successfully' });
        } catch (e: any) {
            setToastState({ show: true, bg: 'danger', message: e?.message || 'Failed to activate customer' });
        }
    };

        const handleDeactivateCustomer = async (id: string) => {
        try {
            await deactivateCustomerMutation.mutateAsync({ customerId: id });
            await refreshCustomerQueries(id);
            setToastState({ show: true, bg: 'success', message: 'Customer deactivated successfully' });
        } catch (e: any) {
            setToastState({ show: true, bg: 'danger', message: e?.message || 'Failed to deactivate customer' });
        }
    };

        const handleBlacklistCustomer = async (id: string) => {
        try {
            await blacklistCustomerMutation.mutateAsync({ customerId: id });
            await refreshCustomerQueries(id);
            setToastState({ show: true, bg: 'success', message: 'Customer blacklisted successfully' });
        } catch (e: any) {
            setToastState({ show: true, bg: 'danger', message: e?.message || 'Failed to blacklist customer' });
        }
    };

        const handleUnblacklistCustomer = async (id: string) => {
        try {
            await unblacklistCustomerMutation.mutateAsync({ customerId: id });
            await refreshCustomerQueries(id);
            setToastState({ show: true, bg: 'success', message: 'Customer unblacklisted successfully' });
        } catch (e: any) {
            setToastState({ show: true, bg: 'danger', message: e?.message || 'Failed to unblacklist customer' });
        }
    };

    const getActionConfig = (action: CustomerActionType) => {
        switch (action) {
            case 'activate':
                return {
                    icon: 'fa fa-user-check',
                    title: 'Confirm Activation',
                    verb: 'activate',
                    confirmLabel: 'Yes, Activate',
                    effects: [
                        'The customer status will be changed to ACTIVE.',
                        'The customer will be eligible to receive new loans again.',
                    ],
                };
            case 'deactivate':
                return {
                    icon: 'fa fa-user-slash',
                    title: 'Confirm Deactivation',
                    verb: 'deactivate',
                    confirmLabel: 'Yes, Deactivate',
                    effects: [
                        'The customer status will be changed to INACTIVE.',
                        'The customer will no longer be eligible to receive new loans.',
                    ],
                };
            case 'blacklist':
                return {
                    icon: 'fa fa-ban',
                    title: 'Confirm Blacklist',
                    verb: 'blacklist',
                    confirmLabel: 'Yes, Blacklist',
                    effects: [
                        'The customer status will be set to BLACKLISTED.',
                        'Use this for high-risk or fraudulent customers.',
                    ],
                };
            case 'unblacklist':
                return {
                    icon: 'fa fa-shield',
                    title: 'Confirm Unblacklist',
                    verb: 'unblacklist',
                    confirmLabel: 'Yes, Unblacklist',
                    effects: [
                        'The customer status will be changed back to ACTIVE.',
                        'The customer will be eligible for normal actions again.',
                    ],
                };
        }
    };

    const executeConfirm = async () => {
        if (!confirmState.id || !confirmState.type || confirmState.loading) return;
        setConfirmState(prev => ({ ...prev, loading: true }));
        if (confirmState.type === 'activate') {
            await handleActivateCustomer(confirmState.id);
        } else if (confirmState.type === 'deactivate') {
            await handleDeactivateCustomer(confirmState.id);
        } else if (confirmState.type === 'blacklist') {
            await handleBlacklistCustomer(confirmState.id);
        } else if (confirmState.type === 'unblacklist') {
            await handleUnblacklistCustomer(confirmState.id);
        }
        closeConfirm(true);
    };

    // New function to handle creating a lead from customer
    const handleCreateLeadFromCustomer = (customer: BusinessCustomer) => {
        // Open the create lead modal
        const modalElement = document.getElementById('CreateLead');
        if (!modalElement) return;
        
        // Add a data attribute to indicate this modal is opened from customer list
        modalElement.setAttribute('data-from-customer', 'true');
        
        // Additionally, store customer data in the modal's dataset to access it later for form validation
        modalElement.setAttribute('data-customer-data', JSON.stringify({
            registrationNumber: customer.info?.registrationNumber || '',
            registeredName: customer.info?.registeredName || '',
            firstName: customer.info?.directors?.[0]?.firstName || '',
            lastName: customer.info?.directors?.[0]?.lastName || '',
            email: customer.contacts?.find(c => c.contactType === 'EMAIL')?.contactValue || '',
            primaryPhone: customer.contacts?.find(c => c.contactType === 'MOBILE')?.contactValue || ''
        }));

        // Pre-populate the lead form with customer data
        const form = document.querySelector('#CreateLead form') as HTMLFormElement;
        if (form) {
            // Set loan type to business since we're dealing with business customers
            const businessRadio = form.querySelector('#business') as HTMLInputElement;
            if (businessRadio) businessRadio.checked = true;

            // Pre-populate business information
            const registrationNumberInput = form.querySelector('input[name="registrationNumber"]') as HTMLInputElement;
            if (registrationNumberInput) {
                registrationNumberInput.value = customer.info?.registrationNumber || '';
                registrationNumberInput.disabled = true;
            }

            const registeredNameInput = form.querySelector('input[name="registeredName"]') as HTMLInputElement;
            if (registeredNameInput) {
                registeredNameInput.value = customer.info?.registeredName || '';
                registeredNameInput.disabled = true;
            }

            // Get the first director to use for contact details if available
            const director = customer.info?.directors && customer.info.directors.length > 0
                ? customer.info.directors[0]
                : null;

            if (director) {
                const firstNameInput = form.querySelector('input[name="firstName"]') as HTMLInputElement;
                if (firstNameInput) {
                    firstNameInput.value = director.firstName || '';
                    firstNameInput.disabled = true;
                }

                const lastNameInput = form.querySelector('input[name="lastName"]') as HTMLInputElement;
                if (lastNameInput) {
                    lastNameInput.value = director.lastName || '';
                    lastNameInput.disabled = true;
                }
            }

            // Try to find email and phone from contacts array if available
            const emailContact = customer.contacts?.find(c => c.contactType === 'EMAIL');
            const phoneContact = customer.contacts?.find(c => c.contactType === 'MOBILE');

            const emailInput = form.querySelector('input[name="email"]') as HTMLInputElement;
            if (emailInput && emailContact) {
                emailInput.value = emailContact.contactValue || '';
                emailInput.disabled = true;
            }

            const phoneInput = form.querySelector('input[name="primaryPhone"]') as HTMLInputElement;
            if (phoneInput && phoneContact) {
                phoneInput.value = phoneContact.contactValue || '';
                phoneInput.disabled = true;
            }

            // Set a default loan amount
            const loanAmountInput = form.querySelector('input[name="loanAmount"]') as HTMLInputElement;
            if (loanAmountInput)
                loanAmountInput.value = '50000'; // Default to 50,000 ZAR
        }

        // Show the modal..
        const modal = new Modal(modalElement);
        modal.show();
    };

        const handleSearch = () => {
        // Set the search parameters to trigger the search
        setPage(1);
        setResetPaginationToggle(prev => !prev);
        console.log('[LoanCustomers] Search params:', {
            ...formValues,
            customerNumber: formValues.customerNumber?.trim() || undefined,
            page: 0,
            size: ALL_CUSTOMERS_PAGE_SIZE
        });
        setSearchParams({
            ...formValues,
            customerNumber: formValues.customerNumber?.trim() || undefined,
            page: 0,
            size: ALL_CUSTOMERS_PAGE_SIZE
        });
    };

    const handleReset = () => {
        // Clear form values
        setFormValues({
            customerNumber: '',
            registeredName: '',
            registrationNumber: '',
            status: undefined,
            createdDateFrom: '',
            createdDateTo: '',
            page: 0,
            size: 10
        });
        // Clear search parameters to return to initial data.
        setSearchParams(null);
        setPage(1);
        setResetPaginationToggle(prev => !prev);
    };

    const getBadgeVariant = (status?: CustomerStatus | string) => {
        const normalized = normalizedStatus(status);
        if (normalized === 'ACTIVE') return 'success';
        if (normalized === 'INACTIVE') return 'danger';
        if (BLACKLISTED_STATUSES.has(normalized)) return 'warning';
        return 'secondary';
    };

    // Keep a balance cache to enable sorting
    const [balanceMap, setBalanceMap] = useState<Record<string, number>>({});
    // Stable identity: passed to a memoised cell, so recreating it each render would undo the memo.
    const setBalanceForCustomer = React.useCallback((customerNumber: string, amount: number) => {
        setBalanceMap(prev => {
            const current = prev[customerNumber];
            if (current === amount) return prev; // avoid unnecessary renders
            return { ...prev, [customerNumber]: amount };
        });
    }, []);

    // Actions menu component using Overlay to portal to body
        const RowActions: React.FC<{ row: BusinessCustomer }> = ({ row }) => {
        const [show, setShow] = useState(false);
        const [measured, setMeasured] = useState(false);
        const targetRef = React.useRef<HTMLButtonElement | null>(null);
        const currentStatus = normalizedStatus(row.status);
        const canActivate = currentStatus === 'INACTIVE';
        const canDeactivate = currentStatus === 'ACTIVE';
        const canBlacklist = currentStatus === 'ACTIVE';
        const canUnblacklist = BLACKLISTED_STATUSES.has(currentStatus);
        const showStatusActions = canActivate || canDeactivate || canBlacklist || canUnblacklist;
        const isConfirmLoadingForRow = !!confirmState.loading && confirmState.id === row.id;

        const close = () => { setShow(false); setMeasured(false); };
        const onToggleClick = (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setMeasured(false);
            setShow((prev) => !prev);
        };

        return (
            <div className="d-flex align-items-center gap-2">
                <button
                    className="btn btn-sm btn-info rounded-circle"
                    onClick={() => navigate(`/loan-customers/${row.id}/documents`)}
                    title="Documents"
                    style={{ width: '36px', height: '36px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                    <i className="fa fa-file"></i>
                </button>

                <button
                    ref={targetRef}
                    type="button"
                    className="btn btn-sm btn-light rounded-pill d-flex align-items-center"
                    onClick={onToggleClick}
                >
                    <span>Actions</span>
                    <i className="fa fa-caret-down ms-2"></i>
                </button>

                                <Overlay
                    show={show}
                    target={targetRef.current}
                    placement="bottom-end"
                    rootClose
                    onHide={close}
                    popperConfig={{ ...customerActionsPopperConfig, onFirstUpdate: () => setMeasured(true) }}
                    container={typeof document !== 'undefined' ? (document.body as any) : undefined}
                >
                    {(props: any) => (
                        <div
                            {...props}
                                                        className="dropdown-menu show actions-menu"
                            style={{ ...props.style, display: 'block', visibility: measured ? 'visible' : 'hidden', opacity: measured ? 1 : 0 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button className="dropdown-item action-view" onClick={() => { handleViewCustomer(row.id || ''); close(); }}>
                                <i className="fa fa-eye"></i>
                                View Customer
                            </button>
                            <button className="dropdown-item action-apply-loan" onClick={() => { handleApplyLoan(row); close(); }}>
                                <i className="fa fa-hand-holding-usd"></i>
                                Apply for Loan
                            </button>
                            <button className="dropdown-item action-edit" onClick={() => { handleEditCustomer(row.id || ''); close(); }}>
                                <i className="fa fa-edit"></i>
                                Edit Customer
                            </button>
                            <button className="dropdown-item action-documents" onClick={() => { navigate(`/loan-customers/${row.id}/documents`); close(); }}>
                                <i className="fa fa-file"></i>
                                Documents
                            </button>
                            <button className="dropdown-item action-accounts" onClick={() => { handleViewAccounts(row.customerNumber || ''); close(); }}>
                                <i className="fa fa-credit-card"></i>
                                View Accounts
                            </button>
                            {showStatusActions && <div className="dropdown-divider" />}
                            {canActivate && (
                                <button className="dropdown-item action-activate" disabled={isConfirmLoadingForRow} onClick={() => { openConfirm('activate', row.id, row.info?.registeredName); close(); }}>
                                    <i className="fa fa-user-check"></i>
                                    Activate
                                </button>
                            )}
                            {canDeactivate && (
                                <button className="dropdown-item action-deactivate" disabled={isConfirmLoadingForRow} onClick={() => { openConfirm('deactivate', row.id, row.info?.registeredName); close(); }}>
                                    <i className="fa fa-user-slash"></i>
                                    Deactivate
                                </button>
                            )}
                            {canBlacklist && (
                                <button className="dropdown-item action-blacklist" disabled={isConfirmLoadingForRow} onClick={() => { openConfirm('blacklist', row.id, row.info?.registeredName); close(); }}>
                                    <i className="fa fa-ban"></i>
                                    Blacklist
                                </button>
                            )}
                            {canUnblacklist && (
                                <button className="dropdown-item action-unblacklist" disabled={isConfirmLoadingForRow} onClick={() => { openConfirm('unblacklist', row.id, row.info?.registeredName); close(); }}>
                                    <i className="fa fa-shield"></i>
                                    Unblacklist
                                </button>
                            )}
                            <div className="dropdown-divider" />
                            <button className="dropdown-item action-opening" onClick={() => { navigate(`/loan-customers/${row.id}/opening-balance`); close(); }}>
                                <i className="fa fa-money"></i>
                                Opening Balance
                            </button>
                        </div>
                    )}
                </Overlay>
            </div>
        );
    };

    const columns: TableColumn<BusinessCustomer>[] = [
        {
            name: 'Customer Number',
            selector: row => row.customerNumber || '',
            sortable: true,
            cell: row => (
                <div className="d-flex align-items-center">
                    <div className="rounded-circle p-2 me-2" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(0,123,255,0.2)' }}>
                        <i className="fa fa-hashtag text-primary" style={{ display: 'block', fontSize: '0.85rem' }}></i>
                    </div>
                    <span className="fw-semibold text-primary">{row.customerNumber || 'N/A '}</span>
                </div>
            ),
            minWidth: '160px',
            maxWidth: '180px',
        },
        {
            name: 'Registered Name',
            selector: row => row.info?.registeredName || '',
            sortable: true,
            cell: row => (
                <div className="fw-semibold" style={{ wordBreak: 'break-word' }}>
                    {row.info?.registeredName || 'N/A'}
                </div>
            ),
            minWidth: '250px',
            wrap: true,
        },
        {
            name: 'Registration Number',
            selector: row => row.info?.registrationNumber || '',
            sortable: true,
            cell: row => (
                <div>
                    {row.info?.registrationNumber ? (
                        <span className="badge bg-light text-dark border px-2 py-1">
                            {row.info.registrationNumber}
                        </span>
                    ) : (
                        <span className="text-muted">N/A</span>
                    )}
                </div>
            ),
            minWidth: '180px',
        },
        {
            name: 'Balance',
            selector: row => row.customerNumber || '',
            sortable: true,
            sortFunction: (rowA, rowB) => {
                const a = balanceMap[rowA.customerNumber || ''] ?? 0;
                const b = balanceMap[rowB.customerNumber || ''] ?? 0;
                return a - b;
            },
            cell: row => (
                <CustomerBalanceCell
                    customerNumber={row.customerNumber}
                    onBalance={setBalanceForCustomer}
                />
            ),
            minWidth: '160px',
        },
        {
            name: 'Status',
            selector: row => displayStatus(row.status),
            sortable: true,
            cell: row => {
                const variant = getBadgeVariant(row.status);
                const statusIcons: Record<string, string> = {
                    'ACTIVE': 'fa-check-circle',
                    'INACTIVE': 'fa-times-circle',
                    'SUSPENDED': 'fa-ban',
                    'BLACKLISTED': 'fa-ban',
                };
                return (
                    <Badge bg={variant} className="px-3 py-2">
                        <i className={`fa ${statusIcons[normalizedStatus(row.status)] || 'fa-circle'} me-1`}></i>
                        {displayStatus(row.status)}
                    </Badge>
                );
            },
            minWidth: '120px',
            maxWidth: '150px',
        },
                {
            name: 'Actions',
            cell: row => (
                <RowActions row={row} />
            ),
            minWidth: '220px',
            allowOverflow: true,
        },
    ];

    // Calculate summary statistics
    const totalCustomers = allCorporateCustomers.length;
    const activeCustomers = allCorporateCustomers.filter(c => c.status === 'ACTIVE').length;
    const inactiveCustomers = allCorporateCustomers.filter(c => c.status === 'INACTIVE').length;
    const blacklistedCustomers = allCorporateCustomers.filter(c => BLACKLISTED_STATUSES.has(normalizedStatus(c.status))).length;
    const confirmActionConfig = confirmState.type ? getActionConfig(confirmState.type) : null;

    return (
        <>
            {/* Header */}
            <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-4 bg-primary-gradient">
                <div className="container-fluid">
                    <div className="row g-3 mb-3 align-items-center">
                        <div className="col">
                            <ol className="breadcrumb bg-transparent mb-0">
                                <li className="breadcrumb-item">
                                    <Link className="text-white" to="/">
                                        <i className="fa fa-home me-2"></i>Home 
                                    </Link> 
                                </li>
                                <li className="breadcrumb-item active text-white" aria-current="page">Business Customers</li>
                            </ol>
                        </div>
                    </div>
                    <div className="row align-items-center">
                        <div className="col-md-6">
                            <h1 className="fs-2 fw-bold mb-2 text-white">
                                Business Customers
                            </h1>
                            <p className="mb-0 text-white fs-6 opacity-75">
                                Manage and search for business customer records 
                            </p>
                        </div>
                        <div className="col-md-6 text-md-end">
                            <div className="d-flex gap-3 justify-content-md-end flex-wrap align-items-center">
                                <Button
                                    variant="light"
                                    className="shadow-lg px-4 py-2 rounded-pill fw-semibold"
                                    style={{
                                        backgroundColor: '#ffffff',
                                        color: '#495057',
                                        border: '2px solid rgba(255, 255, 255, 0.8)',
                                        transition: 'all 0.3s ease',
                                        fontSize: '0.9rem',
                                        minWidth: '140px',
                                        boxShadow: '0 6px 20px rgba(0,0,0,0.2)'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                                        e.currentTarget.style.borderColor = '#ffffff';
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                        e.currentTarget.style.boxShadow = '0 10px 25px rgba(0,0,0,0.25)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = '#ffffff';
                                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.8)';
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.2)';
                                    }}
                                    onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
                                >
                                    <i className={`fa ${showAdvancedSearch ? 'fa-times' : 'fa-search'} me-2`} style={{ fontSize: '0.85rem' }}></i>
                                    {showAdvancedSearch ? 'Hide Search' : 'Advanced Search'}
                                </Button>
                                <Button
                                    variant="info"
                                    className="shadow-lg px-4 py-2 rounded-pill fw-semibold"
                                    style={{
                                        backgroundColor: '#ffffff',
                                        color: '#17a2b8',
                                        border: '2px solid #17a2b8',
                                        transition: 'all 0.3s ease',
                                        fontSize: '0.9rem',
                                        minWidth: '140px',
                                        boxShadow: '0 6px 20px rgba(23, 162, 184, 0.3)'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = '#17a2b8';
                                        e.currentTarget.style.color = '#ffffff';
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                        e.currentTarget.style.boxShadow = '0 10px 25px rgba(23, 162, 184, 0.4)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = '#ffffff';
                                        e.currentTarget.style.color = '#17a2b8';
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 6px 20px rgba(23, 162, 184, 0.3)';
                                    }}
                                    onClick={() => navigate('/loan-application/new')}
                                >
                                    <i className="fa fa-file-text me-2" style={{ fontSize: '0.85rem' }}></i>
                                    New Application
                                </Button>
                                <Button
                                    variant="success"
                                    className="shadow-lg px-4 py-2 rounded-pill fw-semibold"
                                    style={{
                                        backgroundColor: '#ffffff',
                                        color: '#28a745',
                                        border: '2px solid #28a745',
                                        transition: 'all 0.3s ease',
                                        fontSize: '0.9rem',
                                        minWidth: '140px',
                                        boxShadow: '0 6px 20px rgba(40, 167, 69, 0.3)'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = '#28a745';
                                        e.currentTarget.style.color = '#ffffff';
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                        e.currentTarget.style.boxShadow = '0 10px 25px rgba(40, 167, 69, 0.4)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = '#ffffff';
                                        e.currentTarget.style.color = '#28a745';
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 6px 20px rgba(40, 167, 69, 0.3)';
                                    }}
                                    onClick={() => navigate('/loan-customers/new')}
                                >
                                    <i className="fa fa-plus-circle me-2" style={{ fontSize: '0.85rem' }}></i>
                                    Add Customer
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
                <div className="container-fluid">
                    {/* Summary Cards */}
                    <div className="row g-4 mb-4">
                        <div className="col-lg-3 col-md-6">
                            <Card className="border-0 shadow-sm h-100">
                                <Card.Body className="p-4">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <p className="text-muted small mb-1">Total Customers</p>
                                            <h3 className="mb-0 fw-bold text-primary">{totalCustomers}</h3>
                                        </div>
                                        <div className="rounded-circle p-3" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(0,123,255,0.2)' }}>
                                            <i className="fa fa-building fa-2x text-primary" style={{ display: 'block' }}></i>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </div>
                        <div className="col-lg-3 col-md-6">
                            <Card className="border-0 shadow-sm h-100">
                                <Card.Body className="p-4">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <p className="text-muted small mb-1">Active Customers</p>
                                            <h3 className="mb-0 fw-bold text-success">{activeCustomers}</h3>
                                        </div>
                                        <div className="rounded-circle p-3" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(40,167,69,0.2)' }}>
                                            <i className="fa fa-check-circle fa-2x text-success" style={{ display: 'block' }}></i>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </div>
                        <div className="col-lg-3 col-md-6">
                            <Card className="border-0 shadow-sm h-100">
                                <Card.Body className="p-4">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <p className="text-muted small mb-1">Inactive Customers</p>
                                            <h3 className="mb-0 fw-bold text-danger">{inactiveCustomers}</h3>
                                        </div>
                                        <div className="rounded-circle p-3" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(220,53,69,0.2)' }}>
                                            <i className="fa fa-times-circle fa-2x text-danger" style={{ display: 'block' }}></i>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </div>
                        <div className="col-lg-3 col-md-6">
                            <Card className="border-0 shadow-sm h-100">
                                <Card.Body className="p-4">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <p className="text-muted small mb-1">Blacklisted Customers</p>
                                            <h3 className="mb-0 fw-bold text-warning">{blacklistedCustomers}</h3>
                                        </div>
                                        <div className="rounded-circle p-3" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(255,193,7,0.2)' }}>
                                            <i className="fa fa-exclamation-triangle fa-2x text-warning" style={{ display: 'block' }}></i>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </div>
                    </div>

                    {/* Advanced Search Section */}
                    {showAdvancedSearch && (
                        <div className="row mb-4">
                            <div className="col-12">
                                <Card className="border-0 shadow-sm">
                                    <Card.Header className="bg-white border-0 py-3">
                                        <h5 className="mb-0 fw-bold d-flex align-items-center">
                                            <i className="fa fa-search text-primary me-2"></i>
                                            Advanced Search
                                        </h5>
                                    </Card.Header>
                                    <Card.Body>
                                        {error && (
                                            <Alert variant="danger" className="mb-4">
                                                <div className="d-flex align-items-center">
                                                    <i className="fa fa-exclamation-circle me-2"></i>
                                                    <div>
                                                        <h6 className="mb-1">Error Loading Customers</h6>
                                                        <p className="mb-0">{errorMessage}</p>
                                                    </div>
                                                </div>
                                            </Alert>
                                        )}

                                        <Form>
                                            <div className="row g-3">
                                                <div className="col-md-4">
                                                    <Form.Group>
                                                        <Form.Label className="fw-semibold">Customer Number</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            placeholder="Enter customer number"
                                                            value={formValues.customerNumber || ''}
                                                            onChange={(e) => setFormValues(prev => ({ ...prev, customerNumber: e.target.value }))}
                                                        />
                                                    </Form.Group>
                                                </div>
                                                <div className="col-md-4">
                                                    <Form.Group>
                                                        <Form.Label className="fw-semibold">Registered Name</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            placeholder="Enter registered name"
                                                            value={formValues.registeredName}
                                                            onChange={(e) => setFormValues(prev => ({ ...prev, registeredName: e.target.value }))}
                                                        />
                                                    </Form.Group>
                                                </div>
                                                <div className="col-md-4">
                                                    <Form.Group>
                                                        <Form.Label className="fw-semibold">Registration Number</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            placeholder="Enter registration number"
                                                            value={formValues.registrationNumber}
                                                            onChange={(e) => setFormValues(prev => ({ ...prev, registrationNumber: e.target.value }))}
                                                        />
                                                    </Form.Group>
                                                </div>
                                                                                                <div className="col-md-4">
                                                                                                <Form.Group>
                                                                                                    <Form.Label className="fw-semibold">Status</Form.Label>
                                                                                                    <Form.Select
                                                                                                        value={formValues.status || ''}
                                                                                                            onChange={(e) => setFormValues(prev => ({ ...prev, status: e.target.value as SearchCustomersStatus }))}
                                                                                                    >
                                                                                                        <option value="">All</option>
                                                                                                        <option value="ACTIVE">Active</option>
                                                                                                        <option value="INACTIVE">Inactive</option>
                                                                                                        <option value="SUSPENDED">Suspended</option>
                                                                                                        <option value="BLACKLISTED">Blacklisted</option>
                                                                                                        </Form.Select>
                                                                                                    </Form.Group>
                                                                                                </div>
                                                <div className="col-md-4">
                                                    <Form.Group>
                                                        <Form.Label className="fw-semibold">Created Date From</Form.Label>
                                                        <Form.Control
                                                            type="date"
                                                            value={formValues.createdDateFrom}
                                                            onChange={(e) => setFormValues(prev => ({ ...prev, createdDateFrom: e.target.value }))}
                                                        />
                                                    </Form.Group>
                                                </div>
                                                <div className="col-md-4">
                                                    <Form.Group>
                                                        <Form.Label className="fw-semibold">Created Date To</Form.Label>
                                                        <Form.Control
                                                            type="date"
                                                            value={formValues.createdDateTo}
                                                            onChange={(e) => setFormValues(prev => ({ ...prev, createdDateTo: e.target.value }))}
                                                        />
                                                    </Form.Group>
                                                </div>
                                                <div className="col-12">
                                                    <div className="d-flex justify-content-end gap-2">
                                                        <Button variant="outline-secondary" onClick={handleReset}>
                                                            <i className="fa fa-undo me-2"></i>
                                                            Reset
                                                        </Button>
                                                        <Button variant="primary" onClick={handleSearch}>
                                                            <i className="fa fa-search me-2"></i>
                                                            Search
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        </Form>
                                    </Card.Body>
                                </Card>
                            </div>
                        </div>
                    )}

                    {/* Customer Table */}
                    <div className="row">
                        <div className="col-12">
                            <Card className="border-0 shadow-sm">
                                <Card.Header className="bg-white border-0 py-3 px-4">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <h5 className="mb-0 fw-bold d-flex align-items-center">
                                            <i className="fa fa-building text-primary me-2"></i>
                                            Business Customers
                                            {totalCorporateCustomers > 0 && (
                                                <Badge bg="primary" className="ms-2">{totalCorporateCustomers}</Badge>
                                            )}
                                        </h5>
                                        {!showAdvancedSearch && error && (
                                            <Alert variant="danger" className="mb-0 py-2">
                                                <i className="fa fa-exclamation-circle me-2"></i>
                                                {errorMessage}
                                            </Alert>
                                        )}
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    {isLoading ? (
                                        <div className="text-center py-5">
                                            <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                            <p className="mt-3 text-muted">Loading customers...</p>
                                        </div>
                                    ) : (
                                        <div className="table-responsive">
                                            <DataTable
                                                columns={columns}
                                                data={filteredCorporateCustomers}
                                                pagination
                                                paginationDefaultPage={page}
                                                paginationResetDefaultPage={resetPaginationToggle}
                                                highlightOnHover
                                                striped
                                                fixedHeader
                                                fixedHeaderScrollHeight="calc(100vh - 400px)"
                                                paginationPerPage={size}
                                                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                                onChangePage={(nextPage) => setPage(nextPage)}
                                                onChangeRowsPerPage={(newSize) => {
                                                    setSize(newSize);
                                                    setPage(1);
                                                    setFormValues(prev => ({ ...prev, size: newSize }));
                                                }}
                                                paginationComponentOptions={{
                                                    rowsPerPageText: 'Rows per page:',
                                                    rangeSeparatorText: 'of',
                                                    selectAllRowsItem: true,
                                                    selectAllRowsItemText: 'All',
                                                }}
                                                noDataComponent={
                                                    <div className="text-center py-5">
                                                        <i className="fa fa-building fa-3x text-muted mb-3"></i>
                                                        <h5 className="text-muted">No Customers Found</h5>
                                                        <p className="text-muted mb-0">
                                                            {searchParams 
                                                                ? 'Try adjusting your search criteria'
                                                                : 'No business customers available at this time'}
                                                        </p>
                                                    </div>
                                                }
                                                customStyles={{
                                                    table: {
                                                        style: {
                                                            backgroundColor: 'white',
                                                        },
                                                    },
                                                    headRow: {
                                                        style: {
                                                            backgroundColor: '#f8f9fa',
                                                            borderBottom: '2px solid #dee2e6',
                                                            fontWeight: 700,
                                                            fontSize: '0.8rem',
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.5px',
                                                        },
                                                    },
                                                    headCells: {
                                                        style: {
                                                            paddingLeft: '1rem',
                                                            paddingRight: '1rem',
                                                            paddingTop: '1rem',
                                                            paddingBottom: '1rem',
                                                            whiteSpace: 'nowrap',
                                                            fontSize: '0.8rem',
                                                        },
                                                    },
                                                    cells: {
                                                        style: {
                                                            paddingLeft: '1rem',
                                                            paddingRight: '1rem',
                                                            paddingTop: '0.75rem',
                                                            paddingBottom: '0.75rem',
                                                            fontSize: '0.85rem',
                                                        },
                                                    },
                                                    rows: {
                                                        style: {
                                                            minHeight: '55px',
                                                            borderBottom: '1px solid #f8f9fa',
                                                        },
                                                        stripedStyle: {
                                                            backgroundColor: '#ffffff',
                                                        },
                                                    },
                                                }}
                                            />
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </div>
                    </div>
                </div>
            </div>
            {/* Confirm Action Modal */}
            <RBModal
                show={confirmState.show}
                onHide={() => closeConfirm()}
                centered
                backdrop={confirmState.loading ? 'static' : true}
                keyboard={!confirmState.loading}
            >
                <RBModal.Header closeButton={!confirmState.loading} className="bg-success text-white">
                    <RBModal.Title className="d-flex align-items-center gap-2">
                        <i className={confirmActionConfig?.icon || 'fa fa-question-circle'}></i>
                        {confirmActionConfig?.title || 'Confirm Action'}
                    </RBModal.Title>
                </RBModal.Header>
                <RBModal.Body>
                    <div className="text-start">
                        <p className="mb-2 fw-semibold">
                            Are you sure you want to {confirmActionConfig?.verb || 'update'} this customer{confirmState.name ? `: "${confirmState.name}"` : ''}?
                        </p>
                        <p className="text-muted small mb-1 ms-1">If you proceed:</p>
                        <ul className="mt-1 mb-0 small text-muted ps-4 ms-1">
                            {confirmActionConfig?.effects.map((effect) => (
                                <li key={effect} className="mb-1">{effect}</li>
                            ))}
                        </ul>
                    </div>
                </RBModal.Body>
                <RBModal.Footer>
                    <Button
                        variant="success"
                        onClick={executeConfirm}
                        disabled={!!confirmState.loading}
                    >
                        {confirmState.loading && (
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        )}
                        {confirmActionConfig?.confirmLabel || 'Confirm'}
                    </Button>
                    <Button variant="outline-secondary" onClick={() => closeConfirm()} disabled={!!confirmState.loading}>Cancel</Button>
                </RBModal.Footer>
            </RBModal>
            {/* Toasts */}
            <ToastContainer position="top-end" className="p-3">
                <Toast bg={toastState?.bg} show={!!toastState?.show} autohide delay={4000} onClose={() => setToastState(null)}>
                    <Toast.Body className={(toastState?.bg === 'success' || toastState?.bg === 'danger') ? 'text-white d-flex align-items-center' : 'd-flex align-items-center'}>
                        <i className={`fa ${toastState?.bg === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'} me-2`}></i>
                        <span className="fw-semibold">{toastState?.message}</span>
                    </Toast.Body>
                </Toast>
            </ToastContainer>
        </>
    );
};

export default LoanCustomers;
